/**
 * LcoeChartSection — Estimated Levelized Cost of Energy.
 *
 * Renders a vertical recharts BarChart instead of the prior hand-
 * rolled <div> bar list. Each technology row is a stacked bar of
 * `[spacer, range]` so the visible bar represents the low–high
 * cost range (the spacer is transparent and only positions the
 * range bar's left edge at `low`).
 *
 * Site-specific adjustments (solar GHI, wind capacity factor,
 * seismic SDC, grid retail rate) keep the same math the legacy
 * component used. The grid retail rate becomes a ReferenceLine
 * rather than a row in the bar list — that's the read most readers
 * scan for ("does the BTM tech beat grid?"), and rendering it as a
 * dashed reference makes the cross-over instantly visible.
 *
 * Per-tech adjustment captions render BELOW the chart as a small
 * subtitle list. Putting them on the bars themselves (which the
 * legacy version did) collapsed the readable text under PDF
 * rasterisation.
 */
import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Cell,
} from 'recharts';
import { ChartFrame } from '../../ui/ChartFrame';
import { resolveReportChartPalette } from '../../../lib/reportChartTheme';
import { REPORT_SECTION_H2_CLASS, reportSectionHeadingStyle } from '../../../lib/reportSectionHeading';
import { reportSectionGapClass } from '../../../lib/reportSpacingTokens';
import { EditorialSectionShell } from './EditorialSectionShell';
import { GENERIC_SECTION_INSUFFICIENT_MESSAGE } from '../../../lib/sectionFallbacks/derivedFallbacks';
import { LcoeProvenanceFootnote } from './LcoeProvenanceFootnote';
import { ReportSourceNote } from './ReportSourceNote';
import { SiteRagReport } from '../../../types/siteRag';
import { ReportTheme } from '../../../types/reportTheme';
import { formatApproxLcoePerMwh, formatApproxLcoeRange } from '../../../lib/lcoeDisplay';
import { normalizeElectricityPriceToCents, formatElectricityPriceReference } from '../../../lib/reportFormatters';
import {
  composeLcoeExplainer,
  deriveLcoeExplainerRows,
  isLcoeUnavailableInFrozenSource,
} from '../../../lib/lcoe/lcoeExhibitText';
import {
  resolveReportCorrectedLcoe,
  correctedLcoeForBucket,
  type LcoeTechBucket,
} from '../../../lib/finDecision/reportLcoeSurface';
import { INDUSTRY_BASELINES, type BaselineKey } from '../../../services/financial/industryBaselines';
import { roleColor } from '../../../lib/reportRoleColors';
import {
  CHART_FRAME_BOUNDS, CHART_PLOT_MARGIN, CHART_INK,
} from '../../../lib/reportChartLayoutTokens';
import {
  PathwayComparisonReportSection,
  useStudioPreview,
} from '../sections/PathwayComparison/PathwayComparisonReportSection';

/**
 * Industry-norm fallback LCOE per render bucket — used ONLY when no corrected
 * (project-specific) figure resolved, so the bar shows a published baseline
 * AVERAGE rather than the literal "pending". These are NOT invented: each value
 * is the `lcoeNominalPerMwhUsd` already published in the INDUSTRY_BASELINES
 * catalogue (NREL ATB 2024 + Lazard LCOE v17.0 + EIA AEO 2024 + EPRI), the same
 * table the financial-model tech-picker seeds from. The asterisk + footnote on
 * the surface flags these as norm-derived, not site-computed.
 *
 * Bucket → baseline choice:
 *   nuclear → 'smr-noak'   (post-FOAK learning-curve SMR; the representative
 *                           steady-state figure rather than the FOAK premium)
 *   gas     → 'gas-cc'     (base-load combined cycle; the BTM default)
 *   solar   → 'solar-bess' (utility-scale PV + 4-hr storage)
 *   wind    → 'wind-bess'  (onshore wind + 4-hr storage)
 */
const BUCKET_BASELINE_KEY: Partial<Record<LcoeTechBucket, BaselineKey>> = {
  nuclear: 'smr-noak',
  gas: 'gas-cc',
  solar: 'solar-bess',
  wind: 'wind-bess',
};

interface IndustryNormLcoe {
  /** Published baseline LCOE midpoint, USD/MWh. */
  mid: number;
  /** Source citation string from the baseline catalogue. */
  source: string;
}

/** Resolve the published industry-norm LCOE for a bucket, or null when none. */
function industryNormForBucket(bucket: LcoeTechBucket): IndustryNormLcoe | null {
  const key = BUCKET_BASELINE_KEY[bucket];
  if (!key) return null;
  const baseline = INDUSTRY_BASELINES[key];
  const mid = baseline.values.lcoeNominalPerMwhUsd;
  if (typeof mid !== 'number' || !Number.isFinite(mid) || mid <= 0) return null;
  return { mid, source: baseline.source };
}

interface LcoeChartSectionProps {
  report: SiteRagReport;
  theme: ReportTheme;
  isVisible: (sectionId: string) => boolean;
  /**
   * TSP-1 — optional CSS-property bag from the parent renderer
   * (SiteRagReportView). Carries `order` (Studio drag-reorder) and
   * `display:none` (Studio eye toggle) so this section participates in
   * the same propagation surface as every other extracted section.
   * Optional for back-compat with legacy callers that didn't pass it.
   */
  sectionStyle?: (sectionId: string) => React.CSSProperties;
  /** Hero / full-width cover — drop the confidential left rail. */
  omitSectionSidebar?: boolean;
  /** When false, defer padding to a padded page wrapper. */
  shellPadding?: boolean;
  sectionPageColumnCount?: (sectionId: string) => 1 | 2;
  /** N1.5 — VITE_EDITORIAL_LAYOUTS on: render the editorial hero-numeral opener. */
  editorialActive?: boolean;
  /** N1.5 — 1-based TOC display number for the hero numeral (NaN ⇒ none). */
  editorialSectionNumber?: number;
}

interface LcoeRow {
  tech: string;
  low: number;
  high: number;
  /** Spacer bar (transparent) — pushes the range bar's left edge to `low`. */
  spacer: number;
  /** Range = high − low; the visible portion of the stacked bar. */
  range: number;
  /** Caption rendered under the chart (corrected source / pending). */
  note: string;
  /** Index into theme.chartPalette. */
  paletteIdx: number;
  /** No corrected LCOE resolved → render no bar, show "pending" (not a band). */
  pending: boolean;
  /**
   * No corrected figure, but a PUBLISHED industry-norm baseline backs this row:
   * the bar/label shows the norm average with an asterisk + "*pending
   * calculation" footnote. Never a fabricated value — sourced from
   * INDUSTRY_BASELINES (NREL ATB / Lazard / EIA / EPRI).
   */
  industryNorm: boolean;
  /** When `industryNorm`, the baseline source citation (for the footnote). */
  normSource?: string;
}


/**
 * Display string for a row's LCOE value:
 *   - pending (no figure, no baseline) → "pending"
 *   - industry-norm fallback           → "≈$X/MWh avg*" (norm midpoint, asterisk
 *                                         flags the published-baseline source)
 *   - corrected project figure         → "≈$low–$high/MWh" band
 */
function lcoeValueLabel(r: LcoeRow): string {
  if (r.pending) return 'pending';
  if (r.industryNorm) {
    const mid = Math.round((r.low + r.high) / 2);
    return `${formatApproxLcoePerMwh(mid)} avg*`;
  }
  return formatApproxLcoeRange(r.low, r.high);
}

const LcoeChartSection = React.memo<LcoeChartSectionProps>(({ report, theme, isVisible, sectionStyle, omitSectionSidebar = false, shellPadding = true, sectionPageColumnCount, editorialActive = false, editorialSectionNumber }) => {
  // COMBINES (fold #4) — the pinned pathway comparison is FOLDED INTO this
  // section for FEASIBILITY. The pathway data (report.pathwayComparisonAtGen)
  // is unchanged; PathwayComparisonReportSection self-omits when no comparison
  // with 2+ alternatives was pinned (matching the bare-heading guard), so the
  // sub-head only renders when there is real pathway content to show. Studio
  // preview keeps the placeholder so a template author can see + place it.
  const studioPreview = useStudioPreview();
  const pathwaySnapshot = report.pathwayComparisonAtGen;
  const hasPathwayFold =
    report.reportType === 'feasibility'
    && (studioPreview || Boolean(pathwaySnapshot && pathwaySnapshot.pathways.length >= 2));

  if (!isVisible('lcoeChart')) return null;

  const studioStyle: React.CSSProperties = sectionStyle ? sectionStyle('lcoeChart') : {};
  const lcoeUnavailable = isLcoeUnavailableInFrozenSource(report);
  const hasChartData = !!(report.btmOptions && report.btmOptions.length > 0 && report.datasets) && !lcoeUnavailable;

  // COMBINES fold #4 — the folded pathway-comparison sub-block. Rendered beneath
  // the LCOE body (in BOTH the has-data and no-chart-data render paths) under a
  // labelled <h3> sub-head. PathwayComparisonReportSection's inner SectionShell
  // heading is suppressed (suppressHeading) so only its body renders here.
  const pathwayFoldSubBlock = hasPathwayFold ? (
    <div className="space-y-2 pt-5" data-subsection="pathway_comparison">
      <h3
        className="text-[11px] font-semibold uppercase"
        style={{ fontFamily: 'var(--report-body-font, inherit)', letterSpacing: '0.14em', color: roleColor(theme, 'ink') }}
      >
        Pathway Comparison
      </h3>
      <PathwayComparisonReportSection report={report} label="Pathway Comparison" suppressHeading />
    </div>
  ) : null;

  if (!hasChartData) {
    const emptyMessage = lcoeUnavailable
      ? `No site-specific LCOE comparison is available for this report because financial assumptions and capital-structure inputs were not recorded in the frozen source report (section omitted for insufficient evidence; ${formatElectricityPriceReference(report.datasets?.electricity ?? {})} Retained electricity and fuel figures in the power sections are regional context benchmarks only and cannot be compared as all-in served-load LCOE).`
      : GENERIC_SECTION_INSUFFICIENT_MESSAGE;
    return (
      <EditorialSectionShell
        id="lcoeChart"
        data-section="lcoeChart"
        data-section-id="lcoeChart"
        data-section-columns={sectionPageColumnCount ? String(sectionPageColumnCount('lcoeChart')) : undefined}
        extraClassName={reportSectionGapClass(theme.density)}
        style={{
          ...studioStyle,
          fontFamily: theme.bodyFont,
          color: roleColor(theme, 'body'),
          scrollMarginTop: '80px',
          borderBottom: `1px solid ${roleColor(theme, 'hair')}`,
        }}
        data-testid="lcoe-chart-section"
        omitSectionSidebar={omitSectionSidebar}
        shellPadding={shellPadding}
        theme={theme}
        editorialActive={editorialActive}
        sectionNumber={editorialSectionNumber}
        heading="LCOE Cost Comparison"
      >
        {/* Classic mode (flag OFF) — caller-owned head (byte-identical to the
             retired ClassicSectionShell path). Editorial mode: the shell owns the
             opener, so this is suppressed. */}
        {!editorialActive && (
          <h2
            className={`${REPORT_SECTION_H2_CLASS} mb-4`}
            style={reportSectionHeadingStyle(theme)}
            data-heading="true"
          >
            LCOE Cost Comparison
          </h2>
        )}
        <div
          className="text-xs italic px-3 py-2"
          // William QA 2026-06-28 — left accent rule removed report-wide
          style={{
            color: roleColor(theme, 'muted'),
            background: 'transparent',
          }}
          data-testid="lcoe-chart-empty"
        >
          {emptyMessage}
        </div>
        {/* COMBINES fold #4 — pathway comparison still folds in even when the
            LCOE chart itself has no data, so the pinned pathway content is never
            lost just because btmOptions/datasets are absent. */}
        {pathwayFoldSubBlock}
      </EditorialSectionShell>
    );
  }

  // PH-A2 #8: `datasets` is now optional (absent on Firestore-fallback
  // records). Guard at the parent and bail out cleanly if the metadata-
  // only path lands here — the LCOE chart is data-driven, no datasets
  // means no chart.
  const ds = report.datasets!;
  // Canonicalise the retail rate to ¢/kWh BEFORE the $/MWh conversion below.
  // A document-extracted `$0.08/kWh` tariff lands as `retailRate = 0.08`,
  // which would mis-scale the grid ReferenceLine 100× (0.8 $/MWh instead of
  // 80 $/MWh) and disagree with the (now-normalized) Electricity card. Route
  // through the SAME single conversion point the card uses.
  const gridRate = normalizeElectricityPriceToCents(ds.electricity ?? {});

  // TRACK C — corrected per-tech LCOE from the validated finDecision engine
  // (load→nameplate sized; WACC-discounted real $/MWh), preferring the persisted
  // `pathwayLcoeAtGen` (Track B) else a live compute from the gen-time intent.
  // Replaces the prior HARDCODED literature bands (nuclear 65–100 / gas 45–75 /
  // solar 40–80 / wind 35–90) and the GHI/wind-CF/seismic band adjustments. The
  // corrected source is a single $/MWh midpoint per tech, so each bar renders a
  // narrow ±BAND_PCT band around it (purely visual width — the figure is the
  // midpoint). Buckets with no corrected figure fall back to a PUBLISHED
  // industry-norm baseline LCOE (INDUSTRY_BASELINES — NREL ATB / Lazard / EIA /
  // EPRI), drawn as a band around the norm midpoint and flagged "avg*" with a
  // "pending calculation" footnote — never a fabricated band, and only the
  // honest "pending" when even a baseline is unavailable. The grid retail
  // ReferenceLine is a contextual price benchmark; it is not evidence of a
  // measured site tariff. Its existing numeric conversion is unchanged.
  const corrected = resolveReportCorrectedLcoe(report);

  /** Visual half-width of the band drawn around a corrected midpoint (±%). */
  const BAND_PCT = 0.08;

  const ROW_DEFS: Array<{ tech: string; bucket: LcoeTechBucket; paletteIdx: number }> = [
    { tech: 'Nuclear SMR',  bucket: 'nuclear', paletteIdx: 0 },
    { tech: 'Natural Gas',  bucket: 'gas',     paletteIdx: 1 },
    { tech: 'Solar + BESS', bucket: 'solar',   paletteIdx: 2 },
    { tech: 'Wind + BESS',  bucket: 'wind',    paletteIdx: 3 },
  ];

  const rows: LcoeRow[] = ROW_DEFS.map(({ tech, bucket, paletteIdx }) => {
    const entry = correctedLcoeForBucket(corrected, bucket);
    const mid = entry.midUsdPerMwh;
    if (mid == null) {
      // No corrected (project-specific) figure. Rather than the literal
      // "pending", fall back to the PUBLISHED industry-norm baseline LCOE for
      // this technology (INDUSTRY_BASELINES — NREL ATB / Lazard / EIA / EPRI),
      // drawn as a band around the norm midpoint and flagged with an asterisk +
      // "*pending calculation" footnote. No value is fabricated; when even a
      // baseline is unavailable we keep the honest "pending".
      const norm = industryNormForBucket(bucket);
      if (norm) {
        const nLow = Math.round(norm.mid * (1 - BAND_PCT));
        const nHigh = Math.round(norm.mid * (1 + BAND_PCT));
        return {
          tech,
          low: nLow,
          high: nHigh,
          spacer: nLow,
          range: nHigh - nLow,
          // Redhead review wave 3 (defect 4) — the norm row's VALUE is already
          // shown once by lcoeValueLabel ("≈$65/MWh avg*"); the note carries
          // ONLY the qualifier so the legend/tooltip never repeat the same
          // figure twice ("≈$65/MWh avg* · ≈$65/MWh avg* (industry baseline)").
          note: '(industry baseline)',
          paletteIdx,
          pending: false,
          industryNorm: true,
          normSource: norm.source,
        };
      }
      // No corrected figure AND no baseline — render no bar, label "pending".
      return { tech, low: 0, high: 0, spacer: 0, range: 0, note: 'pending', paletteIdx, pending: true, industryNorm: false };
    }
    const low = Math.round(mid * (1 - BAND_PCT));
    const high = Math.round(mid * (1 + BAND_PCT));
    const noteSource = entry.source === 'persisted' ? 'at generation' : 'load-sized';
    return {
      tech,
      low,
      high,
      spacer: low,
      range: high - low,
      note: `${formatApproxLcoePerMwh(mid)} (corrected, ${noteSource})`,
      paletteIdx,
      pending: false,
      industryNorm: false,
    };
  });

  // True when any row fell back to a published industry-norm baseline (drives
  // the asterisk footnote below the captions).
  const hasIndustryNorm = rows.some(r => r.industryNorm);
  // Grid retail $/MWh = retail rate (¢/kWh) × 10. Drives the
  // ReferenceLine; not shown as a bar since it's a single point not
  // a range.
  const gridLine = gridRate != null ? Math.round(gridRate * 10 * 10) / 10 : null;

  // X-axis upper bound: max of all corrected `high` values + the grid line, with
  // a 10 $/MWh padding cap. Falls back to the grid line / a sane default when
  // every bucket is pending so the axis is never degenerate.
  const xMax = Math.max(...rows.map(r => r.high), gridLine ?? 0, 50) + 10;

  // P2.6 — theme palette via the single chart-theme adapter. Legacy
  // (no-preset) reports carry DEFAULT_THEME.chartPalette so they resolve to
  // the same effective colours as before; a preset / tenant palette reskins.
  // Indices 0-3 cover the four BTM techs; index 4 is the grid ReferenceLine.
  const palette = resolveReportChartPalette(theme, 5);

  return (
    <EditorialSectionShell
      id="lcoeChart"
      data-section="lcoeChart"
      data-section-columns={sectionPageColumnCount ? String(sectionPageColumnCount('lcoeChart')) : undefined}
      extraClassName={reportSectionGapClass(theme.density)}
      style={{
        ...studioStyle,
        fontFamily: theme.bodyFont,
        color: roleColor(theme, 'body'),
        scrollMarginTop: '80px',
        borderBottom: `1px solid ${roleColor(theme, 'hair')}`,
      }}
      data-testid="lcoe-chart-section"
      omitSectionSidebar={omitSectionSidebar}
      shellPadding={shellPadding}
      theme={theme}
      editorialActive={editorialActive}
      sectionNumber={editorialSectionNumber}
      heading="LCOE Cost Comparison"
    >
      {/* Classic mode (flag OFF) — caller-owned head (byte-identical to the
           retired ClassicSectionShell path). Editorial mode: the shell owns the
           opener, so this is suppressed. */}
      {!editorialActive && (
        <h2
          className={`${REPORT_SECTION_H2_CLASS} mb-4`}
          style={reportSectionHeadingStyle(theme)}
          data-heading="true"
        >
          LCOE Cost Comparison
        </h2>
      )}

      <div className="w-full" data-testid="lcoe-chart-canvas" data-export-capture="" data-export-id="lcoe-chart-canvas" data-export-title="Levelized Cost of Energy">
        <ChartFrame
          exportId="lcoe-chart-canvas"
          traceName="report:lcoe"
          minWidth={100}
          aspect={CHART_FRAME_BOUNDS.bars.aspect}
          minHeight={CHART_FRAME_BOUNDS.bars.minHeight}
          maxHeight={CHART_FRAME_BOUNDS.bars.maxHeight}
          legibilityFloor={CHART_FRAME_BOUNDS.bars.legibilityFloor}
        >
          <BarChart layout="vertical" data={rows} margin={CHART_PLOT_MARGIN.lcoe} barCategoryGap="34%">
            {/* Grid chrome reads the theme hairline; series Cell fills +
                 ReferenceLine still come from the resolved palette below. */}
            {/* 'rule' (not 'hair', now transparent) so the chart gridlines stay visible. */}
            <CartesianGrid horizontal={false} stroke={roleColor(theme, 'rule')} strokeDasharray="2 4" />
            <XAxis
              type="number"
              domain={[0, xMax]}
              unit=" $/MWh"
              tick={{ fontSize: CHART_INK.axisTickFontSize, fill: roleColor(theme, 'muted') }}
              tickLine={false}
              label={{ value: '$/MWh', position: 'insideBottom', offset: -5, fontSize: CHART_INK.axisLabelFontSize, fill: roleColor(theme, 'muted') }}
            />
            <YAxis
              type="category"
              dataKey="tech"
              tick={{ fontSize: 12, fill: roleColor(theme, 'ink') }}
              tickLine={false}
              axisLine={{ stroke: roleColor(theme, 'rule') }}
              width={100}
            />
            <Tooltip
              cursor={{ fill: 'transparent' }}
              content={({ payload }) => {
                const entry =
                  payload?.find(p => p.dataKey === 'range') ??
                  [...(payload ?? [])].reverse().find(p => (p.payload as LcoeRow | undefined)?.tech != null);
                const r = entry?.payload as LcoeRow | undefined;
                if (!r) return null;
                return (
                  <div
                    className="px-2 py-1 text-xs"
                    style={{
                      background: roleColor(theme, 'subtleBg'),
                      border: `1px solid ${roleColor(theme, 'hair')}`,
                    }}
                  >
                    <div className="font-bold" style={{ color: roleColor(theme, 'ink') }}>{r.tech}</div>
                    <div style={{ color: roleColor(theme, 'muted') }}>{lcoeValueLabel(r)}</div>
                    {r.note && <div style={{ color: roleColor(theme, 'muted') }}>{r.note}</div>}
                  </div>
                );
              }}
            />
            {gridLine != null && (
              <ReferenceLine
                x={gridLine}
                stroke={palette[4] ?? '#A32D2D'}
                strokeDasharray="4 4"
                label={{
                  value: `${ds.electricity?.basis === 'state-industrial-average' ? 'EIA state benchmark' : 'Electricity price reference'} ${formatApproxLcoePerMwh(gridLine)}`,
                  position: 'top',
                  dy: -4,
                  // 11 px = 8.25 pt: the validated chart-label floor is 8 pt. The deck never
                  // measured this label (it rasterised charts first); native print does.
                  fontSize: 11,
                  fill: palette[4] ?? '#A32D2D',
                }}
              />
            )}
            {/* Stacked spacer (transparent) + range (themed). The
                 spacer pushes the visible bar's left edge to `low`. */}
            <Bar isAnimationActive={false} dataKey="spacer" stackId="lcoe" fill="transparent" />
            <Bar isAnimationActive={false} dataKey="range"  stackId="lcoe" radius={[0, 2, 2, 0]}>
              {rows.map((r, i) => (
                <Cell key={`cell-${i}`} fill={palette[r.paletteIdx % palette.length]!} />
              ))}
            </Bar>
          </BarChart>
        </ChartFrame>
      </div>

      {/* SECTION-PORT (D2 → D1 fallback, "cost stack") — the locked LCOE
           layout is a per-technology capex/fuel/O&M/carbon STACK
           (EditorialStackBar). GAP: that cost SPLIT is NOT generated — the
           report carries only a single corrected LCOE figure / band per
           technology (resolveReportCorrectedLcoe → midUsdPerMwh), so the D2
           stack DEGRADES to the documented D1 single-figure table here.
           Composed BENEATH the existing chart head (chart wiring + captions +
           provenance footnote untouched), the table reads the SAME `rows`
           already derived above — no new data, no fabricated split. */}
      {/* GAP: per-technology LCOE cost SPLIT (capex / fuel / O&M / carbon)
              is not emitted by gen — only a single corrected $/MWh midpoint
              (resolveReportCorrectedLcoe) per bucket. When gen emits the split,
              swap this D1 EditorialDataTable for the D2 EditorialStackBar
              (per-tech stacked capex/fuel/O&M/carbon). Do NOT invent the split. */}
      {/* William QA 2026-06-29 — the LCOE range CHART above is canonical; the
          duplicate "Technology / Est. LCOE / Basis" EditorialDataTable that
          re-rendered the same data (the D1 fallback) is removed to kill the
          chart-then-table content duplication. */}

      {/* LCOE-EXPLAIN-1 — the exhibit's write-up: deterministic analysis
           composed from the chart's own rows (see composeLcoeExplainer).
           Reads as body prose under the chart on every surface. */}
      {(() => {
        // Text parity is STRUCTURAL: the same lib derivation feeds DOCX/PPTX,
        // so all three surfaces print the identical write-up.
        const ex = deriveLcoeExplainerRows(report);
        const explainer = composeLcoeExplainer(ex.rows, ex.gridLineUsdPerMwh);
        return explainer ? (
          <p
            className="mt-4 leading-relaxed"
            style={{ color: roleColor(theme, 'body'), fontFamily: theme.bodyFont, fontSize: 13 }}
            data-testid="lcoe-explainer"
          >
            {explainer}
          </p>
        ) : null;
      })()}

      {/* Per-tech adjustment captions — readable in PDF since they
           sit below the chart rather than overlaid on the bars. Chrome
           reads roleColor; the swatch fill stays the resolved palette. */}
      <div
        className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-[10px]"
        style={{ color: roleColor(theme, 'body') }}
        data-testid="lcoe-chart-captions"
      >
        {rows.map(r => (
          <div key={r.tech} className="flex items-baseline gap-2">
            <span
              className="inline-block w-2 h-2 shrink-0 rounded-sm"
              style={{ background: palette[r.paletteIdx % palette.length] }}
              aria-hidden
            />
            <span className="font-bold" style={{ color: roleColor(theme, 'ink') }}>{r.tech}:</span>
            <span>{lcoeValueLabel(r)}</span>
            {/* Redhead wave 3 (defect 4) — the industry-norm note is a bare
                 parenthetical qualifier ("(industry baseline)"), so it reads
                 directly after the value without the "·" separator; corrected
                 rows keep the "· ≈$mid/MWh (corrected, …)" midpoint note. */}
            {r.note && !r.pending && (
              <span style={{ color: roleColor(theme, 'muted') }}>
                {r.industryNorm ? r.note : `· ${r.note}`}
              </span>
            )}
          </div>
        ))}
        {/* Industry-norm asterisk footnote — when any row fell back to a
             PUBLISHED baseline LCOE (no project-specific figure yet), name the
             "avg*" convention + the *pending calculation* status + the cited
             source(s). Lives inside the captions block so the provenance reads
             with the values it qualifies. */}
        {hasIndustryNorm && (
          <ReportSourceNote
            className="col-span-full mt-1"
            testId="lcoe-industry-norm-footnote"
          >
            * Illustrative reference midpoints (drawn with non-evidentiary ±8% graphical display markers; project-specific inputs pending) –
            shown from published baselines (Nuclear SMR reflects steady-state NOAK target vs. FOAK reference table; Solar/Wind + BESS reflect standalone 4-hr storage benchmarks rather than 24/7 firmed AI baseload). See Appendix &amp; References.
          </ReportSourceNote>
        )}
      </div>

      <ReportSourceNote className="mt-2" testId="lcoe-corrected-methodology-note">
        {rows.some(r => !r.industryNorm)
          ? 'Corrected LCOE: WACC-discounted real $/MWh sized to serve the facility load (generation overbuild included), from the project intent. '
          : 'LCOE basis: Illustrative reference midpoints (with non-evidentiary ±8% graphical display markers) shown for technology screening while project-specific contract/tariff and capital-structure inputs are pending reconciliation. '}
        {formatElectricityPriceReference(ds.electricity ?? {})}
      </ReportSourceNote>
      <LcoeProvenanceFootnote report={report} />
      {/* COMBINES fold #4 — pinned pathway comparison, folded beneath the LCOE
          cost comparison under its own sub-head (feasibility only). */}
      {pathwayFoldSubBlock}
    </EditorialSectionShell>
  );
});

LcoeChartSection.displayName = 'LcoeChartSection';

export { LcoeChartSection };
